import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { api, type Me } from '../lib/api';
import './Layout.css';
import './AdminLayout.css';

/**
 * The shell for Milepost staff.
 *
 * Shares Layout.css with the carrier app on purpose — same product, same furniture — but
 * wears a standing marker that says which building you are in. An operator who cannot tell
 * at a glance whether they are looking at the platform or at one carrier's workspace is
 * the person who reads another carrier's numbers as this one's.
 */

interface Props {
  me: Me;
}

const NAV = [
  { to: '/', label: 'Customers', end: true },
  { to: '/activity', label: 'Activity' },
];

export function AdminLayout({ me }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  return (
    <div className="shell is-admin">
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">Milepost</span>
          <span className="brand-sub">Console</span>
        </div>

        <nav aria-label="Sections">
          <ul className="nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.end} className="nav-link">
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-foot">
          <div className="account">
            <span className="account-name">{me.user.fullName}</span>
            <span className="account-role cite">Milepost staff</span>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </aside>

      <main id="main" className="main">
        <Outlet />
      </main>
    </div>
  );
}
